/**
 * QuestionCard  —  question renderer with dark/light theme support
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { sanitizeHtml, stripHtml } from '@/utils/sanitize'

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']

function localText(field, language) {
  if (!field) return ''
  if (typeof field === 'string') return field
  return field[language] ?? field['uz'] ?? field['en'] ?? Object.values(field)[0] ?? ''
}

// ── Type 1 — Single Choice ────────────────────────────────────────────
function SingleChoice({ question, value, onChange, lang }) {
  return (
    <div className="space-y-2">
      {(question.options ?? []).map((opt, i) => {
        const optId   = opt.id ?? opt
        const optText = localText(opt.text ?? opt, lang)
        const checked = value === optId
        return (
          <button
            key={optId}
            onClick={() => onChange(optId)}
            className={[
              'w-full text-left px-4 py-3 rounded-xl border transition-all active:scale-[0.99]',
              'flex items-center gap-3',
              checked
                ? 'border-blue-400 dark:border-blue-500/70 bg-blue-50 dark:bg-blue-900/25'
                : 'border-gray-200 dark:border-[#3a3a3a] bg-white dark:bg-[#252525] hover:border-gray-300 dark:hover:border-[#4a4a4a]',
            ].join(' ')}
          >
            <span className={[
              'w-[18px] h-[18px] rounded-full border-2 flex-none flex items-center justify-center transition-all',
              checked ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-[#555]',
            ].join(' ')}>
              {checked && <span className="w-2 h-2 rounded-full bg-white" />}
            </span>
            <span className={[
              'w-5 text-sm font-bold flex-none',
              checked ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500',
            ].join(' ')}>
              {LETTERS[i] ?? i + 1}
            </span>
            <span
              className={[
                'text-sm flex-1 leading-snug',
                checked ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-gray-300',
              ].join(' ')}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(optText) }}
            />
          </button>
        )
      })}
    </div>
  )
}

// ── Type 2 — Multiple Choice ──────────────────────────────────────────
function MultipleChoice({ question, value = [], onChange, lang }) {
  const selected = Array.isArray(value) ? value : []
  function toggle(optId) {
    const next = selected.includes(optId)
      ? selected.filter((v) => v !== optId)
      : [...selected, optId]
    onChange(next)
  }
  return (
    <div className="space-y-2">
      {(question.options ?? []).map((opt, i) => {
        const optId   = opt.id ?? opt
        const optText = localText(opt.text ?? opt, lang)
        const checked = selected.includes(optId)
        return (
          <button
            key={optId}
            onClick={() => toggle(optId)}
            className={[
              'w-full text-left px-4 py-3 rounded-xl border transition-all active:scale-[0.99]',
              'flex items-center gap-3',
              checked
                ? 'border-blue-400 dark:border-blue-500/70 bg-blue-50 dark:bg-blue-900/25'
                : 'border-gray-200 dark:border-[#3a3a3a] bg-white dark:bg-[#252525] hover:border-gray-300 dark:hover:border-[#4a4a4a]',
            ].join(' ')}
          >
            <span className={[
              'w-[18px] h-[18px] rounded flex-none border-2 flex items-center justify-center transition-all',
              checked ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-[#555]',
            ].join(' ')}>
              {checked && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
            </span>
            <span className={`w-5 text-sm font-bold flex-none ${checked ? 'text-blue-500 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
              {LETTERS[i] ?? i + 1}
            </span>
            <span
              className={`text-sm flex-1 leading-snug ${checked ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-700 dark:text-gray-300'}`}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(optText) }}
            />
          </button>
        )
      })}
    </div>
  )
}

// ── Type 3 — True / False ─────────────────────────────────────────────
function TrueFalse({ value, onChange }) {
  const { t } = useTranslation()
  return (
    <div className="flex gap-3">
      {[
        { val: true,  label: t('common.yes'), icon: '✓', active: 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
        { val: false, label: t('common.no'),  icon: '✕', active: 'border-red-500 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'          },
      ].map(({ val, label, icon, active }) => (
        <button
          key={String(val)}
          onClick={() => onChange(val)}
          className={[
            'flex-1 py-4 rounded-2xl text-base font-bold border-2 transition-all active:scale-95',
            'flex items-center justify-center gap-2',
            value === val
              ? active
              : 'border-gray-200 dark:border-[#3a3a3a] bg-gray-50 dark:bg-[#252525] text-gray-400 hover:border-gray-300 dark:hover:border-[#4a4a4a]',
          ].join(' ')}
        >
          <span>{icon}</span> {label}
        </button>
      ))}
    </div>
  )
}

// ── Type 4 — Short Text ───────────────────────────────────────────────
function ShortText({ value = '', onChange }) {
  const { t } = useTranslation()
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t('exam.types.4')}
      className="
        w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-[#3a3a3a]
        text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-[#252525]
        focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
        transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600
      "
    />
  )
}

// ── Type 5 — Essay ────────────────────────────────────────────────────
function Essay({ value = '', onChange }) {
  const { t } = useTranslation()
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t('exam.types.5')}
      rows={6}
      className="
        w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-[#3a3a3a]
        text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-[#252525] resize-none
        focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10
        transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-600
      "
    />
  )
}

// ── Type 6 — Fill in the Blank ────────────────────────────────────────
function FillBlank({ question, value = [], onChange, lang }) {
  const text   = localText(question.content, lang)
  // Sanitized (strips the legacy import's <style>/<br> boilerplate) *before*
  // splitting, so each rendered part is safe to inject as HTML below.
  const parts  = sanitizeHtml(text).split('___')
  const blanks = parts.length - 1

  function handleChange(idx, val) {
    const next = Array.isArray(value) ? [...value] : Array(blanks).fill('')
    next[idx]  = val
    onChange(next)
  }

  if (blanks === 0) {
    return <ShortText value={value[0] ?? ''} onChange={(v) => onChange([v])} />
  }

  return (
    <div className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed bg-gray-50 dark:bg-[#252525] rounded-xl border border-gray-200 dark:border-[#3a3a3a] px-4 py-4">
      {parts.map((part, i) => (
        <span key={i}>
          <span dangerouslySetInnerHTML={{ __html: part }} />
          {i < blanks && (
            <input
              type="text"
              value={value[i] ?? ''}
              onChange={(e) => handleChange(i, e.target.value)}
              className="
                mx-1 px-2 py-0.5 w-28
                border-b-2 border-blue-500 bg-transparent
                text-blue-600 dark:text-blue-300 font-medium
                focus:outline-none focus:border-blue-400
              "
            />
          )}
        </span>
      ))}
    </div>
  )
}

// ── Type 7 — Matching ─────────────────────────────────────────────────
function Matching({ question, value = [], onChange, lang }) {
  const pairs     = question.options ?? []
  const leftItems = useMemo(() => pairs.map((p) => p.left),  [pairs])
  const rightById = useMemo(() => {
    const m = {}
    pairs.forEach((p) => { m[p.right.id] = p.right })
    return m
  }, [pairs])

  // rightOrder[i] = id of right item currently at row i
  const [rightOrder, setRightOrder] = useState(() => {
    if (Array.isArray(value) && value.length > 0) {
      return leftItems.map((left) => value.find((m) => m.left === left.id)?.right ?? null)
    }
    const arr = pairs.map((p) => p.right.id)
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  })

  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)

  function handleDrop(targetIdx) {
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null); setOverIdx(null); return
    }
    const next = [...rightOrder]
    ;[next[dragIdx], next[targetIdx]] = [next[targetIdx], next[dragIdx]]
    setRightOrder(next)
    onChange(leftItems.map((left, i) => ({ left: left.id, right: next[i] })))
    setDragIdx(null); setOverIdx(null)
  }

  return (
    <div className="space-y-2">
      {leftItems.map((left, i) => {
        const right     = rightById[rightOrder[i]]
        const isDragging = dragIdx === i
        const isOver    = overIdx === i && dragIdx !== i

        return (
          <div key={left.id} className="flex items-center gap-3">
            {/* Left — fixed */}
            <div
              className="flex-1 text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-[#252525] border border-gray-200 dark:border-[#3a3a3a] px-3 py-2.5 rounded-lg"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(localText(left.text, lang)) }}
            />

            <span className="text-gray-400 dark:text-gray-500 text-base shrink-0">→</span>

            {/* Right — draggable */}
            <div
              draggable
              onDragStart={() => { setDragIdx(i); setOverIdx(null) }}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i) }}
              onDragLeave={() => setOverIdx(null)}
              onDrop={() => handleDrop(i)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null) }}
              className={[
                'flex-1 text-sm px-3 py-2.5 rounded-lg border select-none transition-all duration-150',
                'cursor-grab active:cursor-grabbing',
                isDragging
                  ? 'opacity-40 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-200'
                  : isOver
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-gray-700 dark:text-gray-200 scale-[1.02] shadow-sm'
                  : 'border-gray-200 dark:border-[#3a3a3a] bg-white dark:bg-[#252525] text-gray-700 dark:text-gray-200',
              ].join(' ')}
            >
              <span className="flex items-center gap-2">
                <span className="text-gray-300 dark:text-gray-600 text-xs">⠿</span>
                {right
                  ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(localText(right.text, lang)) }} />
                  : '—'}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Type 8 — Ordering ─────────────────────────────────────────────────
function Ordering({ question, value, onChange, lang }) {
  const opts     = question.options ?? []
  const orderIds = value?.order ?? opts.map(o => o.id)
  const ordered  = orderIds.map(id => opts.find(o => o.id === id)).filter(Boolean)

  // Chip mode: all texts ≤ 20 chars → compact inline flow. Measured on the
  // stripped text, not the raw HTML — legacy-imported options carry a fixed
  // `<style>...</style>` boilerplate prefix that would otherwise push every
  // option over the threshold regardless of how short the real answer is.
  const isChipMode = opts.every(o => stripHtml(localText(o.text, lang)).length <= 20)

  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)

  function handleDrop(targetIdx) {
    if (dragIdx === null || dragIdx === targetIdx) {
      setDragIdx(null); setOverIdx(null); return
    }
    const next = [...orderIds]
    const [moved] = next.splice(dragIdx, 1)
    // after removing dragIdx, adjust target index
    const insertAt = dragIdx < targetIdx ? targetIdx - 1 : targetIdx
    next.splice(insertAt, 0, moved)
    onChange({ order: next })
    setDragIdx(null); setOverIdx(null)
  }

  if (isChipMode) {
    return (
      <div className="flex flex-wrap gap-2">
        {ordered.map((opt, idx) => {
          const isDragging = dragIdx === idx
          const isOver     = overIdx === idx && dragIdx !== idx
          return (
            <div
              key={opt.id}
              draggable
              onDragStart={() => { setDragIdx(idx); setOverIdx(null) }}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(idx) }}
              onDragLeave={() => setOverIdx(null)}
              onDrop={() => handleDrop(idx)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null) }}
              className={[
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm select-none',
                'cursor-grab active:cursor-grabbing transition-all duration-150',
                isDragging
                  ? 'opacity-40 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-200'
                  : isOver
                  ? 'border-l-[3px] border-l-blue-500 border-y-gray-200 border-r-gray-200 dark:border-y-[#3a3a3a] dark:border-r-[#3a3a3a] bg-white dark:bg-[#252525] text-gray-700 dark:text-gray-200 shadow-sm'
                  : 'border-gray-200 dark:border-[#3a3a3a] bg-white dark:bg-[#252525] text-gray-700 dark:text-gray-200',
              ].join(' ')}
            >
              <span className="text-gray-300 dark:text-gray-600 text-xs">⠿</span>
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(localText(opt.text, lang)) }} />
            </div>
          )
        })}
      </div>
    )
  }

  // Full-width row mode (long texts)
  return (
    <div className="space-y-2">
      {ordered.map((opt, idx) => {
        const isDragging = dragIdx === idx
        const isOver     = overIdx === idx && dragIdx !== idx
        return (
          <div
            key={opt.id}
            draggable
            onDragStart={() => { setDragIdx(idx); setOverIdx(null) }}
            onDragOver={(e) => { e.preventDefault(); setOverIdx(idx) }}
            onDragLeave={() => setOverIdx(null)}
            onDrop={() => handleDrop(idx)}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null) }}
            className={[
              'flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm select-none',
              'cursor-grab active:cursor-grabbing transition-all duration-150',
              isDragging
                ? 'opacity-40 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-gray-700 dark:text-gray-200'
                : isOver
                ? 'border-t-[3px] border-t-blue-500 border-x-gray-200 border-b-gray-200 dark:border-x-[#3a3a3a] dark:border-b-[#3a3a3a] bg-white dark:bg-[#252525] text-gray-700 dark:text-gray-200 shadow-sm'
                : 'border-gray-200 dark:border-[#3a3a3a] bg-white dark:bg-[#252525] text-gray-700 dark:text-gray-200',
            ].join(' ')}
          >
            <span className="text-gray-300 dark:text-gray-600 text-xs shrink-0">⠿</span>
            <span className="flex-1" dangerouslySetInnerHTML={{ __html: sanitizeHtml(localText(opt.text, lang)) }} />
          </div>
        )
      })}
    </div>
  )
}

// ── Type 9 — Table Completion ─────────────────────────────────────────
function TableCompletion({ question, value, onChange }) {
  const opts   = question.options ?? []
  const ca     = question.correct_answer ?? {}
  const config = ca.table_config ?? {}
  const rows   = config.rows    ?? []
  const cols   = config.cols    ?? []
  const blanks = config.blanks  ?? []
  const cells  = value?.cells   ?? {}

  function setCell(blankId, optionId) {
    onChange({ cells: { ...cells, [blankId]: Number(optionId) } })
  }

  return (
    <div className="space-y-3">
      {rows.length > 0 && cols.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-[#3a3a3a]">
          <table className="min-w-full text-xs text-gray-700 dark:text-gray-300">
            <thead>
              <tr className="border-b border-gray-200 dark:border-[#3a3a3a] bg-gray-50 dark:bg-[#2a2a2a]">
                <th className="px-3 py-2 text-left text-gray-400 dark:text-gray-500"></th>
                {cols.map((col, ci) => (
                  <th key={ci} className="px-3 py-2 text-left text-gray-600 dark:text-gray-400 font-medium">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-b border-gray-100 dark:border-[#2a2a2a]">
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-medium">{row}</td>
                  {cols.map((_, ci) => {
                    const blankIdx = ri * cols.length + ci
                    const blankId  = blanks[blankIdx]
                    if (!blankId) return <td key={ci} className="px-3 py-2 text-gray-300 dark:text-gray-600">—</td>
                    const selected = cells[blankId]
                    return (
                      <td key={ci} className="px-3 py-2">
                        <select
                          value={selected ?? ''}
                          onChange={e => setCell(blankId, e.target.value)}
                          className="w-full text-xs bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#3a3a3a] rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-blue-500"
                        >
                          <option value="">({blankId})</option>
                          {opts.map(o => (
                            <option key={o.id} value={o.id}>{stripHtml(o.text)}</option>
                          ))}
                        </select>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {blanks.length > 0 && (rows.length === 0 || cols.length === 0) && (
        <div className="space-y-2">
          {blanks.map(blankId => (
            <div key={blankId} className="flex items-center gap-3">
              <span className="w-8 text-center font-bold font-mono text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-[#1e2a3a] rounded py-1 text-sm">{blankId}</span>
              <select
                value={cells[blankId] ?? ''}
                onChange={e => setCell(blankId, e.target.value)}
                className="flex-1 text-sm bg-white dark:bg-[#252525] border border-gray-200 dark:border-[#3a3a3a] rounded-xl px-3 py-2 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-blue-500"
              >
                <option value="">—</option>
                {opts.map(o => (
                  <option key={o.id} value={o.id}>{stripHtml(o.text)}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Type 10 — Grouping ────────────────────────────────────────────────
function Grouping({ question, value, onChange, lang }) {
  const opts   = question.options ?? []
  const ca     = question.correct_answer ?? {}
  const groups = ca.groups ?? []
  const assign = value?.assignments ?? {}

  function setAssign(optId, groupId) {
    onChange({ assignments: { ...assign, [optId]: Number(groupId) } })
  }

  return (
    <div className="space-y-3">
      {groups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-1">
          {groups.map(g => (
            <span key={g.id} className="px-3 py-1 rounded-full bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-700 text-pink-600 dark:text-pink-300 text-xs font-medium">
              {g.id}: {g.label}
            </span>
          ))}
        </div>
      )}
      {opts.map(opt => {
        const selected = assign[opt.id]
        return (
          <div key={opt.id} className="flex items-center gap-3 bg-white dark:bg-[#252525] border border-gray-200 dark:border-[#3a3a3a] rounded-xl px-3 py-2">
            <span className="flex-1 text-sm text-gray-700 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: sanitizeHtml(localText(opt.text, lang)) }} />
            <select
              value={selected ?? ''}
              onChange={e => setAssign(opt.id, e.target.value)}
              className="w-40 text-sm bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-[#3a3a3a] rounded-lg px-2 py-1.5 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-pink-500"
            >
              <option value="">— guruh —</option>
              {groups.map(g => (
                <option key={g.id} value={g.id}>{g.label}</option>
              ))}
            </select>
          </div>
        )
      })}
    </div>
  )
}

// ── Type 11 — Multi-blank ─────────────────────────────────────────────
function MultiBlank({ question, value, onChange, lang }) {
  const opts   = question.options ?? []
  const ca     = question.correct_answer ?? {}
  const blanks = ca.blanks ?? []
  const pairs  = value?.pairs ?? {}

  function setPair(blankId, optionId) {
    onChange({ pairs: { ...pairs, [blankId]: optionId } })
  }

  return (
    <div className="space-y-3">
      {blanks.map(blank => (
        <div key={blank.id} className="flex items-center gap-3">
          <span className="w-8 h-8 flex-shrink-0 rounded-full bg-teal-50 dark:bg-teal-900/40 border border-teal-200 dark:border-teal-700 text-teal-600 dark:text-teal-300 flex items-center justify-center text-xs font-bold">
            {blank.id}
          </span>
          <span className="flex-1 text-sm text-gray-600 dark:text-gray-300">{localText(blank.label, lang)}</span>
          <select
            value={pairs[blank.id] ?? ''}
            onChange={e => setPair(blank.id, e.target.value)}
            className="w-48 text-sm bg-white dark:bg-[#252525] border border-gray-200 dark:border-[#3a3a3a] rounded-xl px-3 py-2 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-teal-500"
          >
            <option value="">—</option>
            {opts.map(o => (
              <option key={o.id} value={o.id}>{o.id}: {stripHtml(localText(o.text, lang))}</option>
            ))}
          </select>
        </div>
      ))}
    </div>
  )
}

// ── Type 12 — Multi True/False ────────────────────────────────────────
function MultiTrueFalse({ question, value, onChange, lang }) {
  const { t } = useTranslation()
  const opts     = question.options ?? []
  const verdicts = value?.verdicts ?? {}

  function setVerdict(stmtId, verdict) {
    onChange({ verdicts: { ...verdicts, [stmtId]: verdict } })
  }

  return (
    <div className="space-y-3">
      {opts.map(stmt => {
        const selected = verdicts[stmt.id]
        return (
          <div key={stmt.id} className="flex items-start gap-3 bg-white dark:bg-[#252525] border border-gray-200 dark:border-[#3a3a3a] rounded-xl px-4 py-3">
            <span className="w-8 flex-shrink-0 text-center font-bold text-yellow-500 dark:text-yellow-400 font-mono text-sm mt-0.5">
              {stmt.id}
            </span>
            <span className="flex-1 text-sm text-gray-700 dark:text-gray-200 leading-snug" dangerouslySetInnerHTML={{ __html: sanitizeHtml(localText(stmt.text, lang)) }} />
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setVerdict(stmt.id, true)}
                className={[
                  'px-3 py-1 rounded-full text-xs font-bold border transition-all',
                  selected === true
                    ? 'bg-green-600 border-green-600 text-white'
                    : 'border-gray-200 dark:border-[#3a3a3a] text-gray-400 dark:text-gray-500 hover:border-green-500 hover:text-green-600',
                ].join(' ')}
              >
                {t('common.yes', 'Ha')}
              </button>
              <button
                type="button"
                onClick={() => setVerdict(stmt.id, false)}
                className={[
                  'px-3 py-1 rounded-full text-xs font-bold border transition-all',
                  selected === false
                    ? 'bg-red-600 border-red-600 text-white'
                    : 'border-gray-200 dark:border-[#3a3a3a] text-gray-400 dark:text-gray-500 hover:border-red-500 hover:text-red-600',
                ].join(' ')}
              >
                {t('common.no', "Yo'q")}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main QuestionCard ─────────────────────────────────────────────────
const TYPE_COMPONENTS = {
  1:  SingleChoice,
  2:  MultipleChoice,
  3:  TrueFalse,
  4:  ShortText,
  5:  Essay,
  6:  FillBlank,
  7:  Matching,
  8:  Ordering,
  9:  TableCompletion,
  10: Grouping,
  11: MultiBlank,
  12: MultiTrueFalse,
}

export default function QuestionCard({ question, questionNumber, value, saveStatus, onChange }) {
  const { t, i18n } = useTranslation()
  const lang        = i18n.language

  const QuestionInput = TYPE_COMPONENTS[question.question_type]
  const questionText  = localText(question.content, lang)
  const typeLabel     = t(`exam.types.${question.question_type}`, { defaultValue: '' })
  const hasBlanks     = question.question_type === 6 && questionText.includes('___')

  return (
    <div className="space-y-4 pb-4 max-w-2xl mx-auto w-full">

      {/* ── Header row: Savol N · type badge · score ── */}
      <div className="flex items-center gap-2 flex-wrap">
        {questionNumber != null && (
          <span className="text-xs font-semibold bg-gray-100 dark:bg-[#2e2e2e] border border-gray-200 dark:border-[#3e3e3e] text-gray-500 dark:text-gray-400 px-2.5 py-1 rounded-full">
            Savol {questionNumber}
          </span>
        )}
        {typeLabel && (
          <span className="text-xs font-semibold bg-blue-600 text-white px-2.5 py-1 rounded-full">
            {typeLabel}
          </span>
        )}
        <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
          {question.score} ball
        </span>
      </div>

      {/* ── Question text box ── */}
      {!hasBlanks && questionText && (
        <div
          className="bg-gray-50 dark:bg-[#252525] border border-gray-200 dark:border-[#353535] rounded-xl px-4 py-4 text-gray-800 dark:text-gray-100 text-sm font-medium leading-relaxed prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(questionText) }}
        />
      )}

      {/* ── Answer input ── */}
      {QuestionInput ? (
        <QuestionInput
          question={question}
          value={value}
          onChange={onChange}
          lang={lang}
        />
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">
          Unsupported question type: {question.question_type}
        </p>
      )}
    </div>
  )
}
